import { Link, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { Menu, X, Phone, ChevronDown, Facebook, Instagram } from "lucide-react";
import EuronovaLogo from "./EuronovaLogo";
import { useLanguage } from "@/contexts/LanguageContext";
import WhatsAppIcon from "./WhatsAppIcon";

const FlagRO = () => (
  <svg width="20" height="14" viewBox="0 0 30 20" className="rounded-sm flex-shrink-0">
    <rect width="10" height="20" fill="#002B7F" />
    <rect x="10" width="10" height="20" fill="#FCD116" />
    <rect x="20" width="10" height="20" fill="#CE1126" />
  </svg>
);

const FlagGB = () => (
  <svg width="20" height="14" viewBox="0 0 60 40" className="rounded-sm flex-shrink-0">
    <rect width="60" height="40" fill="#012169" />
    <path d="M0,0 L60,40 M60,0 L0,40" stroke="#fff" strokeWidth="6" />
    <path d="M0,0 L60,40 M60,0 L0,40" stroke="#C8102E" strokeWidth="4" />
    <path d="M30,0 V40 M0,20 H60" stroke="#fff" strokeWidth="10" />
    <path d="M30,0 V40 M0,20 H60" stroke="#C8102E" strokeWidth="6" />
  </svg>
);

const languages = [
  { code: "ro" as const, label: "Română", Flag: FlagRO },
  { code: "en" as const, label: "English", Flag: FlagGB },
];

const LanguageDropdown = ({ mobile = false }: { mobile?: boolean }) => {
  const { lang, setLang } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current = languages.find((l) => l.code === lang)!;

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border hover:bg-muted transition-colors text-sm font-medium"
        style={{ fontFamily: "'Montserrat', sans-serif" }}
        aria-label={`Select language, current: ${lang.toUpperCase()}`}
      >
        <current.Flag />
        <span className="uppercase">{lang}</span>
        <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className={`absolute ${mobile ? "left-0" : "right-0"} mt-1 w-40 rounded-lg border border-border bg-background shadow-lg z-50 overflow-hidden`}>
          {languages.map((l) => (
            <button
              key={l.code}
              onClick={() => { setLang(l.code); setOpen(false); }}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm hover:bg-muted transition-colors ${
                lang === l.code ? "bg-primary/5 font-semibold text-primary" : "text-foreground"
              }`}
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              <l.Flag />
              {l.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Navbar = () => {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t } = useLanguage();

  const navItems = [
    { label: t.nav.aboutUs, path: "/" },
    { label: t.nav.services, path: "/servicii" },
    { label: t.nav.projects, path: "/proiecte" },
    { label: t.nav.contact, path: "/contact" },
  ];

  useEffect(() => {
    const onScroll = () => {};
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg transition-all duration-300">
      <div className="container mx-auto px-6 flex h-20 lg:h-20 items-center justify-center relative">
        <Link to="/" aria-label="Euronova – Pagina principală" className="absolute left-6 h-full py-2">
          <EuronovaLogo className="h-full" />
        </Link>

        <div className="hidden lg:flex items-center gap-8">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`text-lg font-medium tracking-wide transition-colors hover:text-primary ${
                location.pathname === item.path ? "text-primary" : "text-black"
              }`}
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              {item.label}
            </Link>
          ))}

          <div className="flex items-center gap-2">
            <a href="https://www.facebook.com/printariamea" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-border hover:bg-[#1877F2] hover:text-white hover:border-[#1877F2] transition-all duration-200 text-foreground" aria-label="Facebook">
              <Facebook size={18} />
            </a>
            <a href="https://www.instagram.com/euronova2000" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-border hover:bg-gradient-to-br hover:from-[#F58529] hover:via-[#DD2A7B] hover:to-[#8134AF] hover:text-white hover:border-transparent transition-all duration-200 text-foreground" aria-label="Instagram">
              <Instagram size={18} />
            </a>
            <a href="https://wa.me/40722516603" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-border hover:bg-[#25D366] hover:text-white hover:border-[#25D366] transition-all duration-200 text-foreground" aria-label="WhatsApp">
              <WhatsAppIcon size={18} />
            </a>
          </div>

          <LanguageDropdown />

          <a
            href="tel:+40722516603"
            className="inline-flex items-center gap-2 bg-gradient-brand px-4 py-2 rounded-lg text-lg font-semibold text-primary-foreground hover:opacity-90 transition-opacity"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            <Phone size={16} /> {t.nav.callNow}
          </a>
        </div>

        <div className="lg:hidden absolute right-16 flex flex-col items-end gap-0.5">
          <div className="flex items-center gap-1">
            <a href="https://www.facebook.com/printariamea" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-border hover:bg-[#1877F2] hover:text-white hover:border-[#1877F2] transition-all duration-200 text-foreground" aria-label="Facebook">
              <Facebook size={14} />
            </a>
            <a href="https://www.instagram.com/euronova2000" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-border hover:bg-gradient-to-br hover:from-[#F58529] hover:via-[#DD2A7B] hover:to-[#8134AF] hover:text-white hover:border-transparent transition-all duration-200 text-foreground" aria-label="Instagram">
              <Instagram size={14} />
            </a>
            <a href="https://wa.me/40722516603" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-border hover:bg-[#25D366] hover:text-white hover:border-[#25D366] transition-all duration-200 text-foreground" aria-label="WhatsApp">
              <WhatsAppIcon size={14} />
            </a>
          </div>
          <LanguageDropdown />
        </div>

        <button
          className="lg:hidden text-foreground absolute right-6"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-border bg-background/95 backdrop-blur-lg">
          <div className="flex flex-col gap-4 p-6">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                className={`font-display text-lg font-medium ${
                  location.pathname === item.path ? "text-primary" : "text-muted-foreground"
                }`}
              >
                {item.label}
              </Link>
            ))}
            
            <a
              href="tel:+40722516603"
              className="inline-flex items-center justify-center gap-2 bg-gradient-brand px-4 py-2.5 rounded-lg text-base font-semibold text-primary-foreground hover:opacity-90 transition-opacity w-full"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              <Phone size={16} /> {t.nav.callNow}
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
